/**
 * Os seis painéis do quadro por eixos (E78.F3.T03).
 *
 * Cada painel abre com a frase do que aquele eixo confronta — a pergunta que o
 * quadro antigo não respondia — e depois mostra APENAS o que o backend já
 * devolveu. Nenhuma alegação nova nasce aqui: as peças que sabem o que pode ser
 * afirmado (assinatura embutida, cluster, coordenada, linha do tempo) são as
 * mesmas do quadro item a item.
 *
 * As colunas "Referência × Valor comparado" substituem o par Esperado/Obtido,
 * e só aparecem quando algum item do eixo traz os dois lados: no caso real,
 * cinco das sete linhas de identidade exibiam "—" nas duas colunas, dizendo ao
 * perito que havia um confronto onde não havia.
 *
 * E91.F3 (GUS-51 RF07): cada linha diz DE ONDE veio cada lado, pelo catálogo
 * `exame_eletronico_fontes.ts` — "Declarado no protocolo" atribuía ao protocolo
 * o CPF que vem do cadastro do caso (UXD-07). E a frase do que o eixo confronta
 * virou ajuda sob demanda ("Como ler"), para a leitura normal ficar com o
 * achado (RF06).
 */
import { useState, type ReactNode } from 'react';

import {
  Badge,
  Button,
  MonoData,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  ItemVerificacaoData,
  ResultadoExameEletronicoData,
  SituacaoItem,
} from '@/lib/exame_eletronico';
import { fontesDoItem, type FonteDoConfronto } from '@/lib/exame_eletronico_fontes';

import { GeolocalizacaoPrincipal } from './GeolocalizacaoPrincipal';
import { AssinaturaDigital } from './QuadroVerificacaoAssinatura';
import { ClusterCadastral, CoordenadaAvaliada } from './QuadroVerificacaoCluster';
import { ConfrontoDeLocalizacao } from './QuadroVerificacaoLocalizacao';
import { MapasDoExame } from './QuadroVerificacaoMapas';
import { SelosDaPlataforma } from './QuadroVerificacaoSelo';
import { Ausencias, ListaDeIps, RedFlags, SituacaoBadge, Titulo } from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.eixos;
const tq = ptBR.exameEletronico.quadro;
const tfc = ptBR.exameEletronico.fonteDoCodigo;

export interface PainelDoEixoProps {
  resultado: ResultadoExameEletronicoData;
  /** E90.F3 — o caso, para os mapas (rota autenticada). Sem ele, os mapas não saem. */
  caseId?: string | undefined;
}

/**
 * A explicação do que o eixo confronta, sob demanda (E91.F3, RF06).
 *
 * Na leitura normal ficam título, achado, origem e limite; quem quer entender
 * a régua do eixo pede. O texto continua o mesmo do catálogo.
 */
export function ComoLer({ texto, testId }: { texto: string; testId: string }): JSX.Element {
  const [aberto, setAberto] = useState(false);
  return (
    <div data-testid={testId} data-aberto={aberto}>
      <Button
        type="button"
        variant="ghost"
        className="px-0 text-body-sm"
        aria-expanded={aberto}
        onClick={() => setAberto((atual) => !atual)}
        data-testid={`${testId}-alternar`}
      >
        {aberto ? t.ocultarComoLer : t.comoLer}
      </Button>
      {aberto ? <p className="mt-sc-1 max-w-prose text-body-sm text-fg-muted">{texto}</p> : null}
    </div>
  );
}

function rotuloDaFonte(fonte: FonteDoConfronto | null, lado: 'referencia' | 'comparado'): string {
  if (fonte !== null) return t.fontes[fonte];
  return lado === 'referencia' ? t.fonteDesconhecidaReferencia : t.fonteDesconhecidaComparado;
}

/** Rótulo do item por chave; chave desconhecida cai no texto da própria chave. */
function rotuloDoItem(chave: string): string {
  const itens: Record<string, string> = tq.itens;
  return itens[chave] ?? chave;
}

/** O item tem os dois lados do confronto (ou ao menos um deles) para mostrar. */
function temConfronto(item: ItemVerificacaoData): boolean {
  return Boolean(item.esperado) || Boolean(item.obtido);
}

/** Os dois lados do confronto, cada um com a FONTE real no lugar de "Declarado"/"Apurado". */
function CelulaConfronto({ item }: { item: ItemVerificacaoData }): JSX.Element {
  if (!temConfronto(item)) {
    return <span className="text-body-sm text-fg-muted">{item.detalhe ?? '—'}</span>;
  }
  const fontes = fontesDoItem(item.chave);
  return (
    <div className="space-y-sc-1">
      {item.esperado ? (
        <p data-testid={`confronto-${item.chave}-referencia`}>
          <span className="text-caption text-fg-dim">
            {rotuloDaFonte(fontes?.referencia ?? null, 'referencia')}
          </span>{' '}
          <MonoData>{item.esperado}</MonoData>
        </p>
      ) : null}
      {item.fonte_do_esperado ? (
        <p className="text-caption text-fg-dim" data-testid={`confronto-${item.chave}-fonte`}>
          {`${tfc.rotulo}: ${tfc[item.fonte_do_esperado]}`}
        </p>
      ) : null}
      {item.obtido ? (
        <p data-testid={`confronto-${item.chave}-comparado`}>
          <span className="text-caption text-fg-dim">
            {rotuloDaFonte(fontes?.comparado ?? null, 'comparado')}
          </span>{' '}
          <MonoData>{item.obtido}</MonoData>
        </p>
      ) : null}
      {item.detalhe ? <p className="text-body-sm text-fg-muted">{item.detalhe}</p> : null}
    </div>
  );
}

export function FatosDoEixo({
  itens,
  testId,
  children,
}: {
  itens: readonly ItemVerificacaoData[];
  testId: string;
  children?: ReactNode;
}): JSX.Element {
  const algumConfronto = itens.some(temConfronto);
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t.colunaItem}</TableHead>
            <TableHead>{t.colunaSituacao}</TableHead>
            <TableHead>{algumConfronto ? t.colunaConfronto : t.colunaDetalhe}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {itens.map((item) => (
            <TableRow key={item.chave} data-testid={`${testId}-${item.chave}`}>
              <TableCell className="whitespace-nowrap">{rotuloDoItem(item.chave)}</TableCell>
              <TableCell>
                <SituacaoBadge situacao={item.situacao} />
              </TableCell>
              <TableCell className="max-w-[28rem] break-all">
                <CelulaConfronto item={item} />
              </TableCell>
            </TableRow>
          ))}
          {children}
        </TableBody>
      </Table>
    </div>
  );
}

/** Estágios externos daquele eixo, pelas chaves que o backend usa. */
function estagios(
  resultado: ResultadoExameEletronicoData,
  chaves: readonly string[],
): ItemVerificacaoData[] {
  return resultado.enriquecimento.filter((item) => chaves.includes(item.chave));
}

/**
 * A assinatura embutida em UMA linha quando o veredito é "não se aplica".
 *
 * O caso real gastava sete linhas para dizer que o arquivo não trazia
 * assinatura criptográfica. Nos demais vereditos o bloco completo continua
 * inteiro — ali cada linha responde por um fato apurado.
 *
 * E98.F7.T06: o arquivo exportado pela plataforma de envelope também é "não se
 * aplica" (não traz assinatura do signatário), mas o selo dele FOI conferido.
 * A linha não pode dizer que nada foi acionado, e o selo aparece logo abaixo.
 */
function LinhaAssinaturaNaoSeAplica({ comSelo }: { comSelo: boolean }): JSX.Element {
  return (
    <TableRow data-testid="quadro-integridade-assinatura-embutida">
      <TableCell className="whitespace-nowrap">{tq.itens.assinatura_digital_presente}</TableCell>
      <TableCell>
        <Badge variant="neutral">{tq.assinaturaDigitalVereditoOpcoes.nao_se_aplica}</Badge>
      </TableCell>
      <TableCell className="max-w-[28rem] text-body-sm text-fg-muted">
        {comSelo ? t.assinaturaNaoSeAplicaComSelo : t.assinaturaNaoSeAplica}
      </TableCell>
    </TableRow>
  );
}

export function PainelIntegridade({ resultado }: PainelDoEixoProps): JSX.Element {
  const naoSeAplica = resultado.assinatura_digital_veredito === 'nao_se_aplica';
  const apurado = resultado.assinatura_digital;
  const comSelo = (apurado?.selos ?? []).length > 0;
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-integridade">
      <ComoLer texto={t.confronta.integridade} testId="como-ler-integridade" />
      <FatosDoEixo itens={resultado.integridade} testId="quadro-integridade">
        {naoSeAplica ? <LinhaAssinaturaNaoSeAplica comSelo={comSelo} /> : null}
      </FatosDoEixo>
      {naoSeAplica ? (
        apurado ? (
          <SelosDaPlataforma apurado={apurado} />
        ) : null
      ) : (
        <AssinaturaDigital
          apurado={resultado.assinatura_digital}
          veredito={resultado.assinatura_digital_veredito}
        />
      )}
    </div>
  );
}

// `PainelIdentidade` vive em `QuadroPorEixosVinculo.tsx` desde a E84.F3: o
// vínculo da peça importa `FatosDoEixo` daqui, e o painel lá evita o ciclo.

// `PainelTrilha` vive em `QuadroPorEixosJornada.tsx` desde a E84.F4, pela
// mesma razão: a jornada importa `FatosDoEixo` daqui.

export function PainelRede({ resultado }: PainelDoEixoProps): JSX.Element {
  const externos = estagios(resultado, ['classificacao_ip']);
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-rede">
      <ComoLer texto={t.confronta.rede} testId="como-ler-rede" />
      {externos.length > 0 ? (
        <FatosDoEixo itens={externos} testId="quadro-enriquecimento-rede" />
      ) : null}
      <ListaDeIps ips={resultado.ips} />
    </div>
  );
}

/**
 * E168.F3.C01 (QA de 24/10): a coordenada sem raio de precisão é análise limitada, não divergência —
 * "Não confere" ao lado dos 224 m sugeria incompatibilidade que o exame não afirma.
 */
function SituacaoDaCoordenada({ situacao }: { situacao: SituacaoItem }): JSX.Element {
  if (situacao !== 'nao_confere') return <SituacaoBadge situacao={situacao} />;
  return (
    <Badge variant="warning" data-testid="quadro-coordenada-sem-precisao">
      {tq.coordenadaSemRaio}
    </Badge>
  );
}

export function PainelLocalizacao({ resultado, caseId }: PainelDoEixoProps): JSX.Element {
  const externos = estagios(resultado, ['cluster_cadastral', 'geocodificacao']);
  // E182.F1 (ADR-0135 §2.7): com o bureau desligado não há item nem endereço do cluster.
  const bureauConsultado = externos.length > 0 || resultado.cluster.length > 0;
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-localizacao">
      <ComoLer texto={t.confronta.localizacao} testId="como-ler-localizacao" />
      <GeolocalizacaoPrincipal localizacao={resultado.localizacao} />
      <section data-testid="quadro-coordenada">
        <div className="flex items-center gap-sc-2">
          <Titulo>{tq.coordenada}</Titulo>
          <span className="mb-sc-2">
            <SituacaoDaCoordenada situacao={resultado.coordenada_situacao} />
          </span>
        </div>
        {resultado.coordenada ? <CoordenadaAvaliada coordenada={resultado.coordenada} /> : null}
      </section>
      {externos.length > 0 ? (
        <FatosDoEixo itens={externos} testId="quadro-enriquecimento-localizacao" />
      ) : null}
      <ConfrontoDeLocalizacao localizacao={resultado.localizacao} ips={resultado.ips} />
      {caseId ? <MapasDoExame caseId={caseId} mapas={resultado.mapas} /> : null}
      {bureauConsultado ? <ClusterCadastral cluster={resultado.cluster} /> : null}
    </div>
  );
}

export function PainelAlertas({ resultado }: PainelDoEixoProps): JSX.Element {
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-alertas">
      <ComoLer texto={t.confronta.alertas} testId="como-ler-alertas" />
      <RedFlags flags={resultado.red_flags} />
      <Ausencias ausencias={resultado.ausencias} />
    </div>
  );
}
